import { ChevronDown, ChevronRight, Link2, Trash2 } from "lucide-react";
import { Badge } from "./ui/badge";
import LoadFailureNote from "./LoadFailureNote";

/** #568 — human words for a coach_link's status on the admin per-account view. */
export const LINK_STATUS_LABEL = { invited: "Invited", active: "Active", revoked: "Revoked" };

/**
 * Extracted from `AdminUsersPanel` (issue #626 — that component was 47 lines
 * over the `max-lines-per-function` budget). Pure markup for one roster
 * row's coach↔player links (#568): the cache (`linksByUser`), the fetch and
 * the revoke call all still live in `AdminUsersPanel`. This component only
 * renders what it is handed (`linksState`, i.e. `linksByUser[u.id]`) and
 * reports the toggle/retry/revoke intents upward — it makes no requests of
 * its own, and it adds no link-creation affordance (creation stays
 * party-initiated, #288/#506).
 */
export default function AdminUserLinksSection({ u, linksState, onToggle, onRetry, onRevoke, revokingLinkId }) {
  return (
    <div className="mt-2">
      <button
        type="button"
        className="text-[11px] font-semibold text-slate-600 hover:text-slate-900 flex items-center gap-1"
        onClick={onToggle}
        data-testid={`admin-links-toggle-${u.id}`}
      >
        {linksState?.open ? (
          <ChevronDown size={12} className="shrink-0" />
        ) : (
          <ChevronRight size={12} className="shrink-0" />
        )}
        <Link2 size={12} className="shrink-0" />
        <span>
          {linksState?.links
            ? `Links (${linksState.links.length})`
            : "Links"}
        </span>
      </button>
      {linksState?.open && (
        <div className="mt-1.5 pl-4 space-y-1.5" data-testid={`admin-links-${u.id}`}>
          {linksState?.loading && (
            <p className="text-[11px] text-slate-400" data-testid={`admin-links-loading-${u.id}`}>
              Loading links…
            </p>
          )}
          {linksState?.failed && (
            <LoadFailureNote
              what="this account's links"
              onRetry={onRetry}
              testId={`admin-links-failed-${u.id}`}
            />
          )}
          {linksState?.links && linksState.links.length === 0 && (
            <p className="text-[11px] text-slate-400" data-testid={`admin-links-empty-${u.id}`}>
              No coach or player links on this account.
            </p>
          )}
          {linksState?.links?.map((link) => (
            <div
              key={link.id}
              className="flex items-center gap-2 flex-wrap text-[11px]"
              data-testid={`admin-link-${link.id}`}
            >
              <span className="font-semibold text-slate-700 break-words">
                {link.counterpart_display_name}
              </span>
              <Badge variant="outline">
                {link.counterpart_role === "coach" ? "Coach" : "Player"}
              </Badge>
              <span className="text-slate-400">
                {LINK_STATUS_LABEL[link.status] || link.status}
              </span>
              {link.status !== "revoked" && (
                <button
                  type="button"
                  className="underline font-semibold text-red-700 hover:text-red-900 disabled:opacity-50 shrink-0 inline-flex items-center gap-1"
                  disabled={revokingLinkId === link.id}
                  onClick={() => onRevoke(link)}
                  data-testid={`admin-link-revoke-${link.id}`}
                >
                  <Trash2 size={11} className="shrink-0" />
                  {revokingLinkId === link.id ? "Revoking…" : "Revoke"}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
